import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ConfirmModal, Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, BulkSelectCell, TableSkeleton,
  RowActions, RowAction, ListPagination, ListSearchInput, ListEmptyRow, BulkSelectHeader, MultiBulkActionButton,
  StatusDot, btnPrimary, btnDisabled,
} from '@redpanda/ui';
import {
  useBulkActions, createBulkDeleteOptimisticHandler, createBulkDeleteSuccessHandler, createOptimisticBulkDeleteUpdate,
  isAdmin, formatTableDate, useRefetchOnFocus,
} from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { Employee, useEmployeeStoreBase, useArchivedEmployeeStoreBase } from '../stores/employeeStore';
import EmployeeDialog from '../components/EmployeeDialog';
import SalarySettingDialog from '../components/SalarySettingDialog';
import EmployeeLimitBanner from '../components/EmployeeLimitBanner';
import { useEmployeeLimitCache } from '../hooks/useEmployeeLimitCache';
import { useListSearch } from '../hooks/useListSearch';
import { notifyDataChanged } from '../utils/dataEvents';
import { pendingRowClass } from '../utils/pendingItem';
import { employeeTone, toneOf } from '../utils/statusTone';
import { apiErrorMessage } from '../utils/apiError';
import { bulkDeleteEmployees, setEmployeesArchived, getEmployees, getArchivedEmployees } from '../services/api';

interface EmployeeListPageProps {
  isArchived?: boolean;
}

const COLUMNS = 8;

/** 員工列表／已封存員工（照 PM 專案列表：後端搜尋、批次刪除／封存、方案人數上限） */
const EmployeeListPage: React.FC<EmployeeListPageProps> = ({ isArchived = false }) => {
  const { t, i18n } = useTranslation(['pages', 'translation']);
  const { user } = useContext(UserContext)!;
  const userRole = user?.role;
  const admin = isAdmin(userRole);

  const storeBase = isArchived ? useArchivedEmployeeStoreBase : useEmployeeStoreBase;
  const store = storeBase();
  const { items: employees, pagination, isLoading, isPageLoading, showDeleteConfirmModal, isDeleting, fetchItems, setCurrentPage } = store;

  const { canCreateEmployee, setCanCreateEmployee, limitBannerRefreshTrigger, setLimitBannerRefreshTrigger } = useEmployeeLimitCache();

  const [searchTerm, setSearchTerm] = useState('');
  const [dialogEmployee, setDialogEmployee] = useState<Employee | null | undefined>(undefined); // undefined＝關閉、null＝新增
  const [salaryEmployee, setSalaryEmployee] = useState<Employee | null>(null);

  // 切換「員工／已封存」時清掉搜尋
  useEffect(() => setSearchTerm(''), [isArchived]);

  // 有快取先顯示、背景比對（createEntityStore）
  useEffect(() => {
    fetchItems(1);
  }, [fetchItems, isArchived]);
  // 離開 30 秒以上切回分頁時重抓（R33）
  useRefetchOnFocus(() => storeBase.getState().refreshData());

  // 搜尋由後端查全部員工（R29）；沒有搜尋時用列表 store
  const search = searchTerm.trim();
  const fetchList = isArchived ? getArchivedEmployees : getEmployees;
  const searchResult = useListSearch<Employee>('employees', search ? { search, isArchived } : null, page => fetchList(page, { search }));
  const listItems = searchResult.active ? searchResult.items : employees;
  const listPagination = searchResult.active ? searchResult.pagination : pagination;
  const listLoading = searchResult.active ? searchResult.isLoading : isPageLoading;

  const bulkDeleteSuccess = createBulkDeleteSuccessHandler(() => storeBase.getState(), fetchItems, setCurrentPage);
  const bulkActions = useBulkActions({
    items: listItems,
    idField: 'id',
    entityName: t('translation:entityNames.employee'),
    bulkDeleteApi: bulkDeleteEmployees,
    canDeleteItem: () => admin,
    userRole,
    t,
    toast,
    pagination: pagination ? { currentPage: pagination.currentPage, totalPages: pagination.totalPages } : undefined,
    fetchItems,
    setCurrentPage,
    onOptimisticUpdate: createBulkDeleteOptimisticHandler(
      storeBase.setState,
      isArchived ? 'archivedEmployee' : 'employee',
      () => employees,
      () => pagination
    ),
    onSuccess: async (deletedIds?: string[]) => {
      await bulkDeleteSuccess(deletedIds ?? []);
      setLimitBannerRefreshTrigger(prev => prev + 1);
      notifyDataChanged('employees');
    },
  });

  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));

  // 封存／恢復：從目前列表移到另一個列表（照 PM 專案）
  const moveEmployees = async (archived: boolean) => {
    const ids = bulkActions.selectedItems;
    if (ids.length === 0) {
      toast.error(t(archived ? 'pages:employees.list.selectForArchive' : 'pages:employees.list.selectForRestore'));
      return;
    }
    const sourceBase = archived ? useEmployeeStoreBase : useArchivedEmployeeStoreBase;
    const targetBase = archived ? useArchivedEmployeeStoreBase : useEmployeeStoreBase;
    const moved = listItems.filter(e => ids.includes(e.id)).map(e => ({ ...e, isArchived: archived }));

    createOptimisticBulkDeleteUpdate(sourceBase.setState, archived ? 'employee' : 'archivedEmployee')(ids, employees, pagination);
    bulkActions.setSelectedItems([]);

    try {
      const { count } = (await setEmployeesArchived(ids, archived)).data;
      if (count > 0) {
        toast.success(t(archived ? 'pages:employees.list.archiveSuccess' : 'pages:employees.list.restoreSuccess', { count }));
        const target = targetBase.getState();
        const existing = new Set(target.items.map(item => item.id));
        const added = moved.filter(e => !existing.has(e.id));
        if (added.length > 0) {
          targetBase.setState({
            items: [...added, ...target.items],
            pagination: target.pagination ? { ...target.pagination, totalItems: target.pagination.totalItems + added.length } : null,
          });
        }
        setLimitBannerRefreshTrigger(prev => prev + 1);
        notifyDataChanged('employees');
      }
      await sourceBase.getState().refreshData();
    } catch (err: any) {
      console.error(archived ? '封存員工失敗:' : '恢復員工失敗:', err);
      toast.error(apiErrorMessage(err));
      await sourceBase.getState().refreshData();
    }
  };

  const handleConfirmDelete = async () => {
    await store.confirmDelete();
    setLimitBannerRefreshTrigger(prev => prev + 1);
    notifyDataChanged('employees');
  };

  const title = isArchived ? t('pages:employees.archived.title') : t('pages:employees.list.title');

  const addButton = (
    <button
      type="button"
      onClick={() => canCreateEmployee && setDialogEmployee(null)}
      disabled={!canCreateEmployee}
      className={canCreateEmployee ? btnPrimary : btnDisabled}
      title={canCreateEmployee ? undefined : t('pages:errors.EMPLOYEE_LIMIT_REACHED')}
    >
      {t('pages:employees.list.addButton')}
    </button>
  );

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-4">
        {/* 手機寬度兩顆按鈕放不下時整組換到下一行（按鈕文字不折行） */}
        <div className="flex flex-wrap items-baseline justify-between gap-y-3">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          <div className="flex space-x-3 whitespace-nowrap">
            {admin && (
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                isDeleting={bulkActions.isBulkDeleting}
                onDelete={bulkActions.handleBulkDelete}
                onArchive={isArchived ? undefined : () => moveEmployees(true)}
                onRestore={isArchived ? () => moveEmployees(false) : undefined}
              />
            )}
            {!isArchived && addButton}
            <Link to={isArchived ? '/employees' : '/employees/archived'}>
              <Button variant="secondary">
                {isArchived ? t('pages:employees.archived.backToList') : t('translation:common.bulk.viewArchivedAction')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {!isArchived && (
        <EmployeeLimitBanner onCanCreateEmployeeChange={setCanCreateEmployee} refreshTrigger={limitBannerRefreshTrigger} />
      )}

      <div className="mb-4">
        <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('pages:employees.list.searchPlaceholder')} />
      </div>

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mb-3" />

      {(isLoading && employees.length === 0 && !searchResult.active) || listLoading ? (
        <TableSkeleton rows={10} columns={COLUMNS} showHeader={true} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1180px' }}>
              <TableHeader>
                <TableRow>
                  <BulkSelectHeader
                    userRole={userRole}
                    allSelected={bulkActions.allSelected}
                    onToggleAll={bulkActions.toggleSelectAll}
                    hasItems={listItems.length > 0}
                  />
                  <TableHead className="w-32">{t('translation:common.table.columns.name')}</TableHead>
                  <TableHead className="w-32">{t('translation:common.table.columns.employeeNumber')}</TableHead>
                  <TableHead className="w-28">{t('translation:common.table.columns.department')}</TableHead>
                  <TableHead className="w-36">{t('translation:common.table.columns.position')}</TableHead>
                  <TableHead>{t('translation:common.table.columns.contact')}</TableHead>
                  <TableHead numeric className="w-32">{t('translation:common.forms.labels.hireDate')}</TableHead>
                  <TableHead className="w-24">{t('translation:common.table.columns.status')}</TableHead>
                  <TableHead className="w-48">{t('translation:common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listItems.length === 0 ? (
                  <ListEmptyRow
                    colSpan={COLUMNS + 1}
                    filtered={!!search}
                    title={isArchived ? t('pages:employees.archived.empty') : t('pages:employees.list.noEmployees')}
                    hint={isArchived ? t('pages:employees.archived.emptyHint') : t('pages:employees.list.emptyHint')}
                    action={!isArchived && canCreateEmployee && addButton}
                  />
                ) : (
                  listItems.map(employee => (
                    <TableRow key={employee.id} className={pendingRowClass(employee)}>
                      <BulkSelectCell
                        userRole={userRole}
                        isSelected={bulkActions.selectedItems.includes(employee.id)}
                        onToggle={() => bulkActions.toggleItemSelection(employee.id)}
                        canDelete={admin}
                      />
                      <TableCell className="truncate font-medium text-fg" title={employee.name}>{employee.name}</TableCell>
                      {/* 手動輸入的編號可能很長：截斷，完整值放 tooltip（§3-2） */}
                      <TableCell className="truncate tabular-nums" title={employee.employeeNumber || undefined}>
                        {employee.employeeNumber || <span className="text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell className="truncate">{employee.department || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell className="truncate">{employee.position || <span className="text-fg-muted">—</span>}</TableCell>
                      <TableCell className="truncate" title={[employee.email, employee.phone].filter(Boolean).join(' · ') || undefined}>
                        {employee.email || employee.phone ? (
                          <>
                            <span>{employee.email || employee.phone}</span>
                            {employee.email && employee.phone && <span className="block text-[13px] text-fg-secondary tabular-nums">{employee.phone}</span>}
                          </>
                        ) : <span className="text-fg-muted">—</span>}
                      </TableCell>
                      <TableCell numeric>{formatTableDate(employee.hireDate, i18n.language)}</TableCell>
                      <TableCell>
                        <StatusDot tone={toneOf(employeeTone, employee.status)} label={t(`translation:employeeStatus.${employee.status}`)} />
                      </TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setDialogEmployee(employee)}>{t('translation:common.actions.edit')}</RowAction>
                          {/* 薪資設定只給管理員（薪資資料不給一般使用者看） */}
                          {admin && <RowAction onClick={() => setSalaryEmployee(employee)}>{t('pages:salarySetting.action')}</RowAction>}
                          {admin && (
                            <RowAction tone="danger" onClick={() => store.handleDelete(employee.id)}>{t('translation:common.actions.delete')}</RowAction>
                          )}
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <ListPagination pagination={listPagination} onPageChange={changePage} isLoading={listLoading} className="mt-6" />

      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onClose={store.closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={t('pages:employees.list.confirmDeleteTitle')}
        message={t('pages:employees.list.confirmDeleteMessage')}
        confirmText={t('translation:common.actions.delete')}
        cancelText={t('translation:common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('translation:common.status.deleting')}
        t={t}
      />

      <ConfirmModal
        isOpen={bulkActions.showBulkDeleteModal}
        onClose={bulkActions.closeBulkDeleteModal}
        onConfirm={bulkActions.confirmBulkDelete}
        title={t('translation:common.bulk.confirmDeleteTitle', { count: bulkActions.selectedItems.length, entity: t('translation:entityNames.employee') })}
        message={t('pages:employees.list.confirmBulkDeleteMessage', { count: bulkActions.selectedItems.length })}
        confirmText={t('translation:common.actions.delete')}
        cancelText={t('translation:common.actions.cancel')}
        type="danger"
        isLoading={bulkActions.isBulkDeleting}
        loadingText={t('translation:common.status.deleting')}
        t={t}
      />

      {dialogEmployee !== undefined && (
        <EmployeeDialog
          employee={dialogEmployee ?? undefined}
          onClose={() => setDialogEmployee(undefined)}
          onSaved={() => setLimitBannerRefreshTrigger(prev => prev + 1)}
        />
      )}
      {salaryEmployee && <SalarySettingDialog employee={salaryEmployee} onClose={() => setSalaryEmployee(null)} />}
    </div>
  );
};

export default EmployeeListPage;
